import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ContactForm from '../ContactForm';
import { getYouTubeThumbnail } from '../../utils/youtube';
import { GUEST_CHALLENGE_SAMPLES as CHALLENGES, nextChallengeIndex } from '../../constants/guestChallenges';
import { AccentTitle, BrandMark, Eyebrow } from './LandingChrome';
import { DEFAULT_FAQS, DEFAULT_SERVICES, GUEST_STEPS, PROCESS_STEPS, SYSTEMS, WEB_WORKS, WEB_WORK_TYPES } from './landingDefaults';
import { contactInfo, diamondImageStyle, heroDiamondsFrom, isVideoFile, localized, normalizeSrc, pick, scrollToId } from './landingUtils';

const L = (lang, bs, en) => (lang === 'en' ? en : bs);

// An image, or a muted looping clip when the admin saved a video file.
const Media = ({ src, alt = '', eager = false, style }) => (isVideoFile(src)
  ? <video src={src} muted loop autoPlay playsInline preload="metadata" aria-label={alt} style={style} />
  : <img src={src} alt={alt} loading={eager ? 'eager' : 'lazy'} decoding="async" style={style} />);

export const servicesFor = (settings) => (settings?.services?.length > 0 ? settings.services : DEFAULT_SERVICES);

/* ---------- HERO ---------- */
export function LandingHero({ settings, lang }) {
  const eyebrow = pick(settings, 'heroTitle', lang, ['Infinity Creative Agency', 'Infinity Creative Agency']);
  const subtitle = pick(settings, 'heroSubtitle', lang, ['Vaša priča, naš objektiv', 'Your story, our lens']);
  const motto = pick(settings, 'heroMotto', lang, [
    'Godinama se bavimo fotografijom, video produkcijom, web developmentom i kreiranjem aplikacija.',
    'Years of experience in photography, video production, web development and app creation.',
  ]);
  // "Vaša priča, naš objektiv" → heavy first half, italic second half.
  const comma = subtitle.indexOf(',');
  const [lead, tail] = comma > 0
    ? [subtitle.slice(0, comma + 1), subtitle.slice(comma + 1).trim()]
    : [subtitle.split(' ').slice(0, -1).join(' '), subtitle.split(' ').slice(-1)[0]];

  const [left, right] = heroDiamondsFrom(settings);

  return (
    <section className="hero" id="home">
      <div className="wrap">
        <div className="hero-top">
          <Eyebrow>{eyebrow}</Eyebrow>
          <div className="hero-meta">
            <span><strong>{L(lang, 'Foto · Video · Web', 'Photo · Video · Web')}</strong></span>
            <span>{L(lang, 'Bosna i Hercegovina · širom svijeta', 'Bosnia and Herzegovina · worldwide')}</span>
          </div>
        </div>
        <h1 className="h-xl">
          <span className="line"><span>{lead}</span></span>
          {tail && <span className="line"><span className="voice">{tail}</span></span>}
        </h1>
        <div className="stage" aria-hidden="true">
          <div className="hpos left"><div className="hspin"><div className="hframe"><div className="hc2"><Media src={normalizeSrc(left.src)} style={diamondImageStyle(left)} eager /></div></div></div></div>
          <div className="hpos right"><div className="hspin"><div className="hframe"><div className="hc2"><Media src={normalizeSrc(right.src)} style={diamondImageStyle(right)} eager /></div></div></div></div>
          <div className="hdot" />
        </div>
        <div className="hero-cta">
          <p className="hero-desc">{motto}</p>
          <div className="actions">
            <button className="btn btn-gold" onClick={() => scrollToId('contact')}><span className="d" />{L(lang, 'Rezerviši datum', 'Book a date')}</button>
            {settings?.showPackages !== false && (
              <button className="btn btn-ghost" onClick={() => scrollToId('packages')}><span className="d" />{L(lang, 'Pogledaj pakete', 'See packages')}</button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- MARQUEE ---------- */
export function Marquee({ settings, categories, lang }) {
  const words = [
    ...servicesFor(settings).map((s) => (lang === 'en' ? s.titleEn || s.titleBs : s.titleBs)),
    ...(categories || []).map((c) => localized(c.naziv, lang)),
  ].filter(Boolean);
  const unique = [...new Set(words.map((w) => w.trim()))].slice(0, 8);
  if (unique.length === 0) return null;
  const row = unique.map((w, i) => <span key={i} className={i % 2 ? 'o' : ''}>{w}</span>);
  return (
    <div className="marquee" aria-hidden="true">
      <div className="track">{row}{row.map((el, i) => React.cloneElement(el, { key: `b${i}` }))}</div>
    </div>
  );
}

/* ---------- ABOUT ---------- */
export function AboutBlock({ settings, lang }) {
  const title = pick(settings, 'aboutTitle', lang, ['Infinity Creative Studio', 'Infinity Creative Studio']);
  const subtitle = pick(settings, 'aboutSubtitle', lang, ['Fotografija, video, web i aplikacije', 'Photography, video, web and applications']);
  const description = (lang === 'en' ? settings?.aboutDescriptionEn || settings?.storyText1En : settings?.aboutDescriptionBs || settings?.storyText1Bs)
    || L(lang,
      'Već dugo se bavimo fotografijom, video produkcijom, web developmentom i kreiranjem aplikacija.',
      'We have been involved in photography, video production, web development and application creation for a long time.');
  const stats = [
    [settings?.aboutExperience || '10+', L(lang, 'Godina iskustva', 'Years of experience')],
    [settings?.aboutProjects || '500+', L(lang, 'Projekata', 'Projects')],
    [settings?.aboutClients || '300+', L(lang, 'Klijenata', 'Clients')],
  ];
  return (
    <section className="sec" id="about">
      <div className="wrap about">
        <div>
          <Eyebrow>{subtitle}</Eyebrow>
          <AccentTitle text={title} className="h-l" />
        </div>
        <div>
          <p className="lede">{description}</p>
          <div className="stats">
            {stats.map(([n, label]) => <div className="stat" key={label}><b>{n}</b><span>{label}</span></div>)}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- SERVICES ---------- */
export function ServicesBlock({ settings, lang }) {
  const title = pick(settings, 'servicesTitle', lang, ['Kompletna Kreativna Podrška', 'Complete Creative Support']);
  const description = pick(settings, 'servicesDescription', lang, [
    'Od privatnih svečanosti i proslava do korporativnih brending projekata - dokumentujemo svaki važan trenutak sa vrhunskom preciznošću.',
    'From private celebrations to corporate branding projects - we document every important moment with top precision.',
  ]);
  return (
    <section className="sec" id="services">
      <div className="wrap">
        <div className="sec-head split">
          <div>
            <Eyebrow>{L(lang, 'Usluge', 'Services')}</Eyebrow>
            <AccentTitle text={title} />
          </div>
          <p className="lede">{description}</p>
        </div>
        <div className="services">
          {servicesFor(settings).map((s, i) => (
            <article className="svc" key={i}>
              <div className="ico" aria-hidden="true"><i /><i /></div>
              <div>
                <h3 className="h-m">{lang === 'en' ? s.titleEn || s.titleBs : s.titleBs}</h3>
                <p>{lang === 'en' ? s.descriptionEn || s.descriptionBs : s.descriptionBs}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- WORKS: featured projects + portfolio tiles from settings ---------- */
export function WorksBlock({ settings, lang, projects }) {
  const portfolio = settings?.showPortfolio === false ? [] : [1, 2, 3, 4]
    .filter((n) => settings?.[`portfolioItem${n}Image`])
    .map((n) => ({
      key: `p${n}`,
      title: pick(settings, `portfolioItem${n}Title`, lang),
      image: normalizeSrc(settings[`portfolioItem${n}Image`]),
      href: settings[`portfolioItem${n}Link`] || '/projekti',
    }));
  const items = [
    ...projects.map((p) => ({
      key: p.id,
      title: p.title,
      category: p.category,
      image: p.image ? normalizeSrc(p.image) : (p.link ? getYouTubeThumbnail(p.link) : ''),
      to: `/projekti/${p.id}`,
    })),
    ...portfolio,
  ].filter((i) => i.image);
  if (items.length === 0) return null;

  const title = pick(settings, 'portfolioTitle', lang, ['Naš Portfelj', 'Our Portfolio']);
  return (
    <section className="sec tight" id="projects">
      <div className="wrap">
        <div className="sec-head split">
          <div>
            <Eyebrow>{L(lang, 'Radovi', 'Work')}</Eyebrow>
            <AccentTitle text={title} />
          </div>
          <p className="lede">{L(lang, 'Svaki kadar počinje kao romb i otvara se dok skrolate.', 'Every frame starts as a diamond and opens as you scroll.')}</p>
        </div>
        <div className="work">
          {items.map((it) => {
            const inner = (
              <>
                <div className="clip"><Media src={it.image} alt={it.title} /></div>
                <div className="shade" />
                <div className="cap"><strong>{it.title}</strong>{it.category && <span>{it.category}</span>}</div>
              </>
            );
            if (it.to) return <Link className="shot" key={it.key} to={it.to}>{inner}</Link>;
            return it.href.startsWith('http')
              ? <a className="shot" key={it.key} href={it.href} target="_blank" rel="noopener noreferrer">{inner}</a>
              : <Link className="shot" key={it.key} to={it.href}>{inner}</Link>;
          })}
        </div>
        <div className="swipe"><i />{L(lang, 'Prevuci za još', 'Swipe for more')}</div>
        <div className="work-foot"><Link className="btn btn-ghost" to="/projekti"><span className="d" />{L(lang, 'Svi projekti', 'All projects')}</Link></div>
      </div>
    </section>
  );
}

/* ---------- GALLERY + LIGHTBOX ---------- */
export function Lightbox({ images, index, onClose, onMove }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onMove(1);
      if (e.key === 'ArrowLeft') onMove(-1);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose, onMove]);
  return (
    <div className="lightbox" role="dialog" aria-modal="true" onClick={onClose}>
      <img src={images[index]} alt="" onClick={(e) => e.stopPropagation()} />
      <button className="lb-btn lb-close" onClick={onClose} aria-label="Zatvori" />
      <button className="lb-btn lb-prev" onClick={(e) => { e.stopPropagation(); onMove(-1); }} aria-label="Prethodna"><i /></button>
      <button className="lb-btn lb-next" onClick={(e) => { e.stopPropagation(); onMove(1); }} aria-label="Sljedeća"><i /></button>
      <div className="lb-count">{index + 1} / {images.length}</div>
    </div>
  );
}

export function GalleryBlock({ settings, lang }) {
  const images = (settings?.galleryImages || []).filter(Boolean).map(normalizeSrc).filter((s) => !isVideoFile(s));
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null);
  const move = useCallback((d) => setOpen((i) => (i + d + images.length) % images.length), [images.length]);
  const close = useCallback(() => setOpen(null), []);
  if (images.length === 0) return null;
  const visible = showAll ? images : images.slice(0, 7);
  const title = pick(settings, 'galleryTitle', lang, ['Trenuci koje smo zabilježili', 'Moments we registered']);
  const subtitle = pick(settings, 'gallerySubtitle', lang, ['Svaka fotografija je priča koja ostaje.', 'Every photograph is a story that stays.']);
  return (
    <section className="sec tight" id="gallery">
      <div className="wrap">
        <div className="sec-head split">
          <div>
            <Eyebrow>{L(lang, 'Galerija', 'Gallery')}</Eyebrow>
            <AccentTitle text={title} />
          </div>
          <p className="lede">{subtitle}</p>
        </div>
        <div className="mosaic">
          {visible.map((src, i) => (
            <button className="shot" key={src + i} onClick={() => setOpen(i)} aria-label={L(lang, `Otvori fotografiju ${i + 1}`, `Open photo ${i + 1}`)}>
              <div className="clip"><img src={src} alt="" loading="lazy" decoding="async" /></div>
            </button>
          ))}
        </div>
        {images.length > 7 && (
          <div className="more">
            <button className="btn btn-ghost" onClick={() => setShowAll((v) => !v)}><span className="d" />
              {showAll ? L(lang, 'Prikaži manje', 'Show less') : L(lang, `Prikaži sve (${images.length})`, `Show all (${images.length})`)}
            </button>
          </div>
        )}
      </div>
      {open !== null && <Lightbox images={images} index={open} onClose={close} onMove={move} />}
    </section>
  );
}

/* ---------- FILMS ---------- */
export function Film({ film }) {
  const [playing, setPlaying] = useState(false);
  const poster = normalizeSrc(film.thumbnail) || (film.type === 'youtube' ? getYouTubeThumbnail(film.url) : '');
  if (playing) {
    return (
      <div className="film">
        {film.type === 'youtube'
          ? <iframe src={`${film.embed}${film.embed.includes('?') ? '&' : '?'}autoplay=1`} title={film.title || 'Film'} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
          : <video src={film.embed} controls autoPlay playsInline />}
      </div>
    );
  }
  return (
    <div className="film">
      {poster ? <img src={poster} alt="" loading="lazy" /> : film.type === 'file' && <video src={film.embed} muted preload="metadata" />}
      <button className="play" onClick={() => setPlaying(true)} aria-label={`Pusti: ${film.title || 'film'}`}>
        <i />
        {film.title && <span className="title">{film.title}</span>}
      </button>
    </div>
  );
}

export function FilmsBlock({ settings, lang, films }) {
  if (films.length === 0) return null;
  const title = pick(settings, 'filmsTitle', lang, ['Filmovi', 'Films']);
  const description = pick(settings, 'filmsDescription', lang, [
    'Pogledajte kako naš rad izgleda - činematografske priče koje ostaju zauvijek.',
    'See what our work looks like - cinematic stories that last forever.',
  ]);
  return (
    <section className="sec tight" id="videos">
      <div className="wrap">
        <div className="sec-head split">
          <div>
            <Eyebrow>{L(lang, 'Video', 'Video')}</Eyebrow>
            <h2 className="h-l">{title}</h2>
          </div>
          <p className="lede">{description}</p>
        </div>
        <div className={`films${films.length === 1 ? ' one' : ''}`}>{films.map((f) => <Film key={f.id} film={f} />)}</div>
        <div className="work-foot">
          {settings?.youtubeChannelUrl
            ? <a className="btn btn-ghost" href={settings.youtubeChannelUrl} target="_blank" rel="noopener noreferrer"><span className="d" />YouTube</a>
            : <Link className="btn btn-ghost" to="/films"><span className="d" />{L(lang, 'Svi filmovi', 'All films')}</Link>}
        </div>
      </div>
    </section>
  );
}

/* ---------- MOTTO ---------- */
export function MottoBand({ lang }) {
  return (
    <section className="band" aria-label={L(lang, 'Moto', 'Motto')}>
      <div className="wrap">
        <BrandMark className="mark-lg" id="bandMark" width={92} stroke={3} />
        <p>{L(lang, 'Trenuci koji traju', 'Moments that last')} <em>{L(lang, 'zauvijek', 'forever')}</em></p>
      </div>
    </section>
  );
}

/* ---------- PROCESS ---------- */
export function ProcessBlock({ lang }) {
  return (
    <section className="sec tight" id="proces">
      <div className="wrap">
        <div className="sec-head">
          <Eyebrow>{L(lang, 'Kako radimo', 'How we work')}</Eyebrow>
          <h2 className="h-l">{L(lang, 'Od upita do', 'From inquiry to')} <span className="voice">{L(lang, 'galerije', 'gallery')}</span></h2>
        </div>
        <div className="steps">
          <div className="rail"><i /></div>
          {PROCESS_STEPS.map((s, i) => {
            const [t, d] = s[lang];
            return (
              <div className="step" key={t}>
                <div className="node"><i /></div>
                <div><span className="num">{String(i + 1).padStart(2, '0')}</span><h3 className="h-m">{t}</h3></div>
                <p>{d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- QR GUEST GALLERY ---------- */
export function GuestsBlock({ lang }) {
  const [index, setIndex] = useState(0);
  const [rolling, setRolling] = useState(false);
  const roll = () => {
    if (rolling) return;
    setRolling(true);
    setTimeout(() => { setIndex((p) => nextChallengeIndex(p)); setRolling(false); }, 650);
  };
  return (
    <section className="sec guests" id="galerija-gostiju">
      <div className="wrap qr">
        <div>
          <Eyebrow>{L(lang, 'QR galerija za goste', 'QR guest gallery')}</Eyebrow>
          <h2 className="h-l" style={{ marginTop: 22 }}>{L(lang, 'Svaki gost', 'Every guest')} <span className="voice">{L(lang, 'postaje', 'becomes a')}</span> {L(lang, 'fotograf', 'photographer')}</h2>
          <div className="qr-list">
            {GUEST_STEPS.map((s) => {
              const [t, d] = s[lang];
              return <div key={t}><i /><div><b>{t}</b><p>{d}</p></div></div>;
            })}
          </div>
          <Link className="btn btn-ghost" to="/qr-galerija"><span className="d" />{L(lang, 'Saznaj više', 'Learn more')}</Link>
        </div>
        <div className="phone" aria-label={L(lang, 'Primjer ekrana QR galerije', 'Example of the QR gallery screen')}>
          <div className="notch" />
          <div className="screen">
            <BrandMark tone="light" width={46} />
            <small>{L(lang, 'Fotografije gostiju', 'Guest photos')}</small>
            <div className="names">Amila i Tarik</div>
            <div className={`dice${rolling ? ' rolling' : ''}`} aria-hidden="true"><i /><b /></div>
            <div className="task" aria-live="polite"><strong>{L(lang, 'VAŠ ZADATAK', 'YOUR TASK')}</strong>{CHALLENGES[index]}</div>
            <button className="roll" onClick={roll}>{L(lang, 'Izvuci zadatak', 'Draw a task')}</button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- SYSTEMS + WEB WORK ---------- */
const host = (url) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');

function WebWorks({ lang }) {
  const i = lang === 'en' ? 1 : 0;
  const [type, setType] = useState('all');
  const types = ['all', ...Object.keys(WEB_WORK_TYPES).filter((t) => WEB_WORKS.some((w) => w.type === t))];
  const list = type === 'all' ? WEB_WORKS : WEB_WORKS.filter((w) => w.type === type);
  return (
    <div className="webworks">
      <div className="chips-row" role="tablist" aria-label={L(lang, 'Vrsta projekta', 'Project type')}>
        {types.map((t) => {
          const count = t === 'all' ? WEB_WORKS.length : WEB_WORKS.filter((w) => w.type === t).length;
          return (
            <button key={t} role="tab" aria-selected={type === t} className={`chip${type === t ? ' on' : ''}`} onClick={() => setType(t)}>
              {t === 'all' ? L(lang, 'Sve', 'All') : WEB_WORK_TYPES[t][i]} <small>{count}</small>
            </button>
          );
        })}
      </div>
      <ul className="weblist">
        {list.map((w) => {
          const inner = (
            <>
              <span className="wl-type">{WEB_WORK_TYPES[w.type][i]}</span>
              <strong className="wl-name">{w.name}</strong>
              <span className="wl-desc">{w[lang]}</span>
              <span className="wl-host">{w.url ? host(w.url) : 'infinitycreative.agency' + w.to}</span>
              <span className="wl-go" aria-hidden="true"><i /></span>
            </>
          );
          return (
            <li key={w.name}>
              {w.to
                ? <Link to={w.to}>{inner}</Link>
                : <a href={w.url} target="_blank" rel="noopener noreferrer">{inner}</a>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function SystemsBlock({ lang }) {
  const i = lang === 'en' ? 1 : 0;
  return (
    <section className="sec" id="sistemi">
      <div className="wrap">
        <div className="sec-head split">
          <div>
            <Eyebrow>{L(lang, 'Web i sistemi', 'Web and systems')}</Eyebrow>
            <h2 className="h-l">{L(lang, 'Gradimo i', 'We also build')} <span className="voice">{L(lang, 'softver', 'software')}</span></h2>
          </div>
          <p className="lede">{L(lang,
            'Web stranice, aplikacije i sistemi za rezervacije. Kod i pokret radimo pod istim krovom.',
            'Websites, apps and booking systems. Code and motion under one roof.')}</p>
        </div>
        <div className="systems">
          {SYSTEMS.map((s) => (
            <article className="sys" key={s.to}>
              <span className="badge">{s.badge[i]}</span>
              <h3 className="h-m">{s.title[i]} <span className="voice">{s.accent[i]}</span></h3>
              <p className="lede" style={{ fontSize: 16 }}>{s.text[i]}</p>
              <div className="chips">{s.pills.map((p) => <span key={p}>{p}</span>)}</div>
              <Link className="btn btn-ghost" to={s.to}><span className="d" />{L(lang, 'Pogledaj sistem', 'See the system')}</Link>
            </article>
          ))}
        </div>
        <WebWorks lang={lang} />
      </div>
    </section>
  );
}

/* ---------- PACKAGES ---------- */
const hoursLabel = (h, lang) => {
  if (lang === 'en') return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  return `${h} ${h === 1 ? 'sat' : h >= 2 && h <= 4 ? 'sata' : 'sati'}`;
};

export function PackagesBlock({ settings, lang, packages, categories, onSelect }) {
  if (!packages || packages.length === 0) return null;
  const tag = pick(settings, 'packagesTag', lang, ['Paketi Usluga', 'Service Packages']);
  const title = pick(settings, 'packagesTitle', lang, ['Odaberite Savršen Paket', 'Choose the Perfect Package']);
  const description = pick(settings, 'packagesDescription', lang, [
    'Pažljivo kreirani paketi koji spajaju kreativnost, profesionalnost i tehnologiju za svaki vaš važan projekat.',
    'Carefully created packages that combine creativity, professionalism, and technology for your every important project.',
  ]);
  return (
    <section className="sec tight" id="packages">
      <div className="wrap">
        <div className="sec-head split">
          <div>
            <Eyebrow>{tag}</Eyebrow>
            <AccentTitle text={title} />
          </div>
          <p className="lede">{description}</p>
        </div>
        <div className="packs">
          {packages.map((pkg) => {
            const cat = (categories || []).find((c) => c.slug === pkg.category);
            const hours = Number(pkg.brojSati) > 0 ? Number(pkg.brojSati) : null;
            const duration = hours ? hoursLabel(hours, lang) : localized(pkg.trajanje, lang);
            const catLabel = cat ? `${cat.ikona || ''} ${localized(cat.naziv, lang)}`.trim()
              : pkg.tipUsluge === 'foto' ? 'Foto' : pkg.tipUsluge === 'video' ? 'Video' : 'Premium';
            const name = localized(pkg.naziv, lang);
            return (
              <article className="pack" key={pkg.firestoreId}>
                <Link to={`/paketi/${pkg.firestoreId}`} className="pimg" aria-label={name}>
                  <img src={normalizeSrc(pkg.slika) || '/img/gallery/6.webp'} alt="" loading="lazy" />
                  <span className="cat">{catLabel}</span>
                </Link>
                <div className="pbody">
                  <h3 className="h-m">{name}</h3>
                  {pkg.opis && <p className="pdesc">{localized(pkg.opis, lang)}</p>}
                  {duration && <div className="meta"><span>{duration}</span></div>}
                  <div className="price">{pkg.cijena ? `${pkg.cijena} ${pkg.valuta || ''}`.trim() : L(lang, 'Na upit', 'On request')}</div>
                  <div className="pactions">
                    <Link className="btn btn-ghost btn-sm" to={`/paketi/${pkg.firestoreId}`}>{L(lang, 'Detalji', 'Details')}</Link>
                    <button className="btn btn-gold btn-sm" onClick={() => onSelect(pkg)}>{L(lang, 'Upit', 'Inquire')}</button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
        <div className="packs-foot">
          <button className="btn btn-ghost" onClick={() => onSelect({ naziv: L(lang, 'Dodatne Ponude', 'Additional Offers'), firestoreId: 'additional' })}>
            <span className="d" />{L(lang, 'Treba vam nešto drugo? Pitajte nas', 'Need something else? Ask us')}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
export function FaqBlock({ settings, lang }) {
  const faqs = settings?.faqs?.length > 0 ? settings.faqs : DEFAULT_FAQS;
  const [open, setOpen] = useState(0);
  const title = pick(settings, 'faqTitle', lang, ['Često Postavljena Pitanja', 'Frequently Asked Questions']);
  const subtitle = pick(settings, 'faqSubtitle', lang, ['Još uvijek imate pitanje? Kontaktirajte nas!', 'Still have a question? Contact us!']);
  return (
    <section className="sec tight" id="faq">
      <div className="wrap faq">
        <div>
          <Eyebrow>FAQ</Eyebrow>
          <AccentTitle text={title} className="h-l" />
          <p className="lede" style={{ marginTop: 22 }}>{subtitle}</p>
        </div>
        <div className="faq-list">
          {faqs.map((f, i) => {
            const q = lang === 'en' ? f.questionEn || f.questionBs : f.questionBs;
            const a = lang === 'en' ? f.answerEn || f.answerBs : f.answerBs;
            const isOpen = open === i;
            return (
              <div className={`qa${isOpen ? ' open' : ''}`} key={i}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen} aria-controls={`faq-${i}`}>
                  <span>{q}</span><i aria-hidden="true" />
                </button>
                <div className="ans" id={`faq-${i}`} role="region"><div><p>{a}</p></div></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- REVIEWS ---------- */
// Desktop shows three whole reviews; with more than three the rail advances
// one card every few seconds and pauses while the visitor hovers or focuses it.
function ReviewsRail({ reviews, lang }) {
  const railRef = useRef(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (reviews.length <= 3 || paused) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setInterval(() => {
      const rail = railRef.current;
      if (!rail || !window.matchMedia('(min-width: 961px)').matches) return;
      const card = rail.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + 16 : rail.clientWidth / 3;
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + step, behavior: 'smooth' });
    }, 4500);
    return () => clearInterval(id);
  }, [reviews.length, paused]);
  return (
    <div
      className="reviews"
      ref={railRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {reviews.map((r) => (
        <figure className="review" key={r.firestoreId}>
          <div className="stars" aria-label={`${r.rating || 5}/5`}>
            {[1, 2, 3, 4, 5].map((n) => <i key={n} className={n <= (r.rating || 5) ? '' : 'off'} />)}
          </div>
          <blockquote>{r.text}</blockquote>
          <cite>{r.clientName || L(lang, 'Klijent', 'Client')}</cite>
        </figure>
      ))}
    </div>
  );
}

export function ReviewsBlock({ settings, lang, reviews }) {
  if (reviews.length === 0) return null;
  const title = pick(settings, 'reviewsTitle', lang, ['Šta kažu naši klijenti', 'What Our Clients Say']);
  const subtitle = pick(settings, 'reviewsSubtitle', lang, ['Iskrena mišljenja onih koje smo imali čast fotografisati.', 'Honest words from those we had the honour of photographing.']);
  return (
    <section className="sec tight" id="recenzije">
      <div className="wrap">
        <div className="sec-head split">
          <div>
            <Eyebrow>{L(lang, 'Recenzije', 'Reviews')}</Eyebrow>
            <AccentTitle text={title} />
          </div>
          <p className="lede">{subtitle}</p>
        </div>
        <ReviewsRail reviews={reviews} lang={lang} />
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
export function FinalCta({ settings, lang }) {
  const title = pick(settings, 'ctaTitle', lang, ['Spremni da započnete vašu priču?', 'Ready to start your story?']);
  const subtitle = pick(settings, 'ctaSubtitle', lang, ['Rezervišite termin danas i osigurajte vrhunsku realizaciju vaših ideja.', 'Book a date today and ensure the top realization of your ideas.']);
  return (
    <section className="final" id="final-cta">
      <div className="wrap">
        <div className="mk" aria-hidden="true"><div className="f a" /><div className="f b" /><div className="p" /></div>
        <AccentTitle text={title} className="h-xl" />
        <p className="lede">{subtitle}</p>
        <div className="actions">
          <button className="btn btn-gold" onClick={() => scrollToId('contact')}><span className="d" />{L(lang, 'Rezerviši datum', 'Book a date')}</button>
          {settings?.showPackages !== false && <button className="btn btn-ghost" onClick={() => scrollToId('packages')}><span className="d" />{L(lang, 'Pogledaj pakete', 'See packages')}</button>}
        </div>
      </div>
    </section>
  );
}

/* ---------- CONTACT ---------- */
export function ContactBlock({ settings, lang, selectedPackage }) {
  const info = contactInfo(settings);
  return (
    <section className="sec contact" id="contact">
      <div className="wrap contact-grid">
        <div>
          <Eyebrow>{L(lang, 'Kontakt', 'Contact')}</Eyebrow>
          <h2 className="h-l" style={{ marginTop: 22 }}>{L(lang, 'Zakažite', 'Book')} <span className="voice">{L(lang, 'vaš', 'your')}</span> {L(lang, 'termin', 'date')}</h2>
          <div className="contact-lines">
            <a href={`tel:${info.phone}`}><small>{L(lang, 'Telefon', 'Phone')}</small><strong>{info.phoneRaw}</strong></a>
            <a href={`mailto:${info.email}`}><small>Email</small><strong>{info.email}</strong></a>
            <a href={info.whatsapp} target="_blank" rel="noopener noreferrer"><small>WhatsApp</small><strong>{L(lang, 'Pišite nam', 'Message us')}</strong></a>
            {info.instagram && <a href={info.instagram} target="_blank" rel="noopener noreferrer"><small>Instagram</small><strong>{info.instagram.replace(/^https?:\/\/(www\.)?instagram\.com\//, '@').replace(/\/$/, '')}</strong></a>}
          </div>
        </div>
        <div className="contact-form"><ContactForm prefilledPackage={selectedPackage} /></div>
      </div>
    </section>
  );
}
